import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { BackButton, HomeButton } from '../components/NavButtons';
import { getCurrencySymbol, getSymbol } from '../utils/currency';
import VoiceSearchButton from '../components/VoiceSearchButton';
import { SearchMap } from '../components/MapComponents';
import {
  Car, Bed, Barbell, FirstAidKit, House, Buildings, UsersThree, CalendarBlank,
  Camera, Package, Storefront, ShoppingBag, Bank, TreeStructure, Church, Scissors,
  Martini, ForkKnife, SoccerBall, Lightning, MagnifyingGlass, MapPin, Star,
  Heart, ArrowLeft, Funnel, X, List as ListIcon, MapTrifold, SortAscending, HandHeart, GraduationCap, VideoCamera, Clock, Users, MoonStars
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'parking', name: 'Parking', icon: Car, gradient: 'bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-600' },
  { id: 'hotels', name: 'Hotels', icon: Bed, gradient: 'bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500' },
  { id: 'gyms', name: 'Gyms', icon: Barbell, gradient: 'bg-gradient-to-br from-red-400 via-rose-500 to-pink-600' },
  { id: 'yoga_wellness', name: 'Yoga / Wellness', icon: HandHeart, gradient: 'bg-gradient-to-br from-pink-400 via-fuchsia-500 to-purple-600' },
  { id: 'astrology', name: 'Astrology', icon: MoonStars, gradient: 'bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-800' },
  { id: 'hospital', name: 'Hospital', icon: FirstAidKit, gradient: 'bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600' },
  { id: 'home', name: 'Home', icon: House, gradient: 'bg-gradient-to-br from-amber-300 via-amber-500 to-orange-600' },
  { id: 'coworking', name: 'Coworking', icon: Buildings, gradient: 'bg-gradient-to-br from-indigo-400 via-violet-500 to-purple-600' },
  { id: 'meeting_rooms', name: 'Meeting Rooms', icon: UsersThree, gradient: 'bg-gradient-to-br from-cyan-400 via-sky-500 to-blue-600' },
  { id: 'event_spaces', name: 'Events', icon: CalendarBlank, gradient: 'bg-gradient-to-br from-fuchsia-400 via-pink-500 to-rose-600' },
  { id: 'studios', name: 'Studios', icon: Camera, gradient: 'bg-gradient-to-br from-violet-400 via-purple-500 to-fuchsia-600' },
  { id: 'storage', name: 'Storage', icon: Package, gradient: 'bg-gradient-to-br from-slate-400 via-slate-600 to-slate-800' },
  { id: 'shops', name: 'Shops', icon: Storefront, gradient: 'bg-gradient-to-br from-orange-400 via-red-500 to-pink-600' },
  { id: 'malls', name: 'Malls', icon: ShoppingBag, gradient: 'bg-gradient-to-br from-pink-400 via-rose-500 to-red-600' },
  { id: 'halls', name: 'Halls', icon: Bank, gradient: 'bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-600' },
  { id: 'housing_societies', name: 'Societies', icon: TreeStructure, gradient: 'bg-gradient-to-br from-teal-400 via-emerald-500 to-green-600' },
  { id: 'religious_places', name: 'Religious', icon: Church, gradient: 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600' },
  { id: 'salons', name: 'Salons', icon: Scissors, gradient: 'bg-gradient-to-br from-fuchsia-400 via-pink-500 to-purple-600' },
  { id: 'clubs', name: 'Clubs', icon: Martini, gradient: 'bg-gradient-to-br from-purple-400 via-violet-600 to-indigo-700' },
  { id: 'restaurants', name: 'Restaurants', icon: ForkKnife, gradient: 'bg-gradient-to-br from-red-400 via-orange-500 to-amber-500' },
  { id: 'sports', name: 'Sports', icon: SoccerBall, gradient: 'bg-gradient-to-br from-lime-400 via-green-500 to-emerald-600' },
  { id: 'ev_charging', name: 'EV Charging', icon: Lightning, gradient: 'bg-gradient-to-br from-yellow-300 via-lime-400 to-green-500' },
  { id: 'education', name: 'Education', icon: GraduationCap, gradient: 'bg-gradient-to-br from-blue-400 via-indigo-500 to-violet-600' }
];

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recommended');
  const [priceRange, setPriceRange] = useState([0, searchParams.get('max_price') ? parseInt(searchParams.get('max_price')) : 10000]);
  const [instantBooking, setInstantBooking] = useState(searchParams.get('instant_booking') === 'true');
  const [sessionType, setSessionType] = useState(searchParams.get('session_type') || '');
  const [availableSessionTypes, setAvailableSessionTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [liveClassCategories, setLiveClassCategories] = useState([]);
  const viewTab = (searchParams.get('view') === 'live') ? 'live' : 'spaces';
  const [liveClasses, setLiveClasses] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [wishlist, setWishlist] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'

  // Fetch which categories have Live Sessions enabled (for tab visibility)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/platform/settings`);
        if (!cancelled) setLiveClassCategories(res.data?.live_class_categories || []);
      } catch (err) { console.warn('Failed to load platform settings:', err?.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  // When 'Live Sessions' tab is active, fetch them for this category + filters
  useEffect(() => {
    if (viewTab !== 'live' || !category) return;
    let cancelled = false;
    setLiveLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ category, status: 'upcoming' });
        if (user?.country && !cancelled) params.set('country', user.country);
        const res = await axios.get(`${API_URL}/api/live-classes?${params.toString()}`);
        let list = res.data?.classes || [];
        // Filter by session type client-side (matches session_type field on class doc)
        if (sessionType) {
          const st = sessionType.toLowerCase();
          list = list.filter(c => (c.session_type || '').toLowerCase() === st);
        }
        if (!cancelled) setLiveClasses(list);
      } catch (err) { if (!cancelled) setLiveClasses([]); }
      finally { if (!cancelled) setLiveLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [viewTab, category, user, sessionType]);

  const setViewTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'live') params.set('view', 'live');
    else params.delete('view');
    setSearchParams(params);
  };

  // Fetch session types whenever the category changes (or clears)
  useEffect(() => {
    if (!category) { setAvailableSessionTypes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/session-types?category=${category}`);
        const list = (res.data?.session_types || []).map(s => s.name).filter(Boolean);
        if (!cancelled) setAvailableSessionTypes(list);
      } catch (err) { if (!cancelled) setAvailableSessionTypes([]); }
    })();
    return () => { cancelled = true; };
  }, [category]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (category) params.append('category', category);
      if (location) params.append('location', location);
      if (sortBy) params.append('sort_by', sortBy);
      if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
      if (priceRange[1] < 10000) params.append('max_price', priceRange[1]);
      if (instantBooking) params.append('instant_booking', 'true');
      if (sessionType) params.append('session_type', sessionType);
      params.append('limit', '20');

      const response = await axios.get(`${API_URL}/api/search?${params.toString()}`);
      setListings(response.data.listings || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [query, category, location, sortBy, priceRange, instantBooking, sessionType]);

  const fetchWishlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wishlist`);
      setWishlist(response.data.listings?.map(l => l.listing_id) || []);
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  }, []);

  useEffect(() => {
    fetchListings();
    if (user) fetchWishlist();
  }, [searchParams, fetchListings, fetchWishlist, user]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    if (sortBy !== 'recommended') params.set('sort', sortBy);
    setSearchParams(params);
  };

  const handleCategorySelect = (cat) => {
    setCategory(cat);
    setSessionType(''); // reset session type when category changes
    const params = new URLSearchParams(searchParams);
    if (cat) {
      params.set('category', cat);
    } else {
      params.delete('category');
    }
    params.delete('session_type');
    // Clear view=live if the new category does not have Live Sessions enabled
    if (!cat || !liveClassCategories.includes(cat)) {
      params.delete('view');
    }
    setSearchParams(params);
  };

  const handleSessionTypeChange = (st) => {
    setSessionType(st);
    const params = new URLSearchParams(searchParams);
    if (st) params.set('session_type', st);
    else params.delete('session_type');
    setSearchParams(params);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    setSearchParams(params);
  };

  const applyFilters = () => {
    fetchListings();
    setShowFilters(false);
  };

  const toggleWishlist = async (listingId, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      if (wishlist.includes(listingId)) {
        await axios.delete(`${API_URL}/api/wishlist/${listingId}`);
        setWishlist(prev => prev.filter(id => id !== listingId));
      } else {
        await axios.post(`${API_URL}/api/wishlist/${listingId}`, {});
        setWishlist(prev => [...prev, listingId]);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center h-16 gap-4">
            <BackButton />
            <HomeButton />
            
            <form onSubmit={handleSearch} className="flex-1">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search spaces..."
                    className="w-full pl-12 pr-4 h-11 bg-slate-50 border-slate-200 rounded-full"
                    data-testid="search-input"
                  />
                </div>
                <VoiceSearchButton onResult={(text) => { setQuery(text); const p = new URLSearchParams(searchParams); p.set('q', text); setSearchParams(p); }} size="sm" />
              </div>
            </form>

            <Button
              variant="outline"
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 rounded-full"
              data-testid="filter-btn"
            >
              <Funnel size={18} />
              <span className="hidden sm:inline">Filters</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Category Pills — vibrant 3D treatment */}
      <div className="bg-white/60 backdrop-blur-xl border-b border-slate-200/60 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex gap-2">
            <button
              onClick={() => handleCategorySelect('')}
              className={`relative shrink-0 overflow-hidden px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-150 ring-1 ${
                !category
                  ? 'bg-gradient-to-br from-indigo-400 via-violet-500 to-fuchsia-600 text-white ring-violet-300/50 shadow-[0_4px_0_-1px_rgba(91,33,182,0.45),0_8px_18px_-4px_rgba(168,85,247,0.55)] -translate-y-0.5'
                  : 'bg-white text-slate-700 ring-slate-200/70 shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_-2px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_10px_-2px_rgba(99,102,241,0.18)]'
              }`}
              data-testid="category-all-btn"
            >
              {!category && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />}
              <span className="relative z-10">All Categories</span>
            </button>
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSelected = category === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.id)}
                  className={`relative shrink-0 overflow-hidden flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-150 ring-1 ${
                    isSelected
                      ? `${cat.gradient || 'bg-gradient-to-br from-pink-400 via-fuchsia-500 to-purple-600'} text-white ring-pink-300/50 shadow-[0_4px_0_-1px_rgba(157,23,77,0.45),0_8px_18px_-4px_rgba(236,72,153,0.55)] -translate-y-0.5`
                      : 'bg-white text-slate-700 ring-slate-200/70 shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_-2px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_10px_-2px_rgba(236,72,153,0.18)]'
                  }`}
                  data-testid={`category-pill-${cat.id}`}
                >
                  {isSelected && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />}
                  <Icon size={16} className="relative z-10" weight={isSelected ? 'fill' : 'regular'} />
                  <span className="relative z-10">{cat.name}</span>
                </button>
              );
            })}
          </div>
          {category && availableSessionTypes.length > 0 && (
            <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="session-type-chips">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1.5 px-2 py-0.5 rounded-full bg-white/70 ring-1 ring-slate-200/60 shadow-sm">
                {getCategoryInfo(category).name} →
              </span>
              <button onClick={() => handleSessionTypeChange('')}
                className={`relative overflow-hidden px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150 ring-1 ${
                  !sessionType
                    ? 'bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white ring-slate-500/60 shadow-[0_3px_0_-1px_rgba(0,0,0,0.5),0_6px_14px_-3px_rgba(15,23,42,0.55)] -translate-y-0.5'
                    : 'bg-white text-slate-600 ring-slate-200/70 shadow-sm hover:-translate-y-0.5 hover:shadow-[0_4px_10px_-2px_rgba(99,102,241,0.18)] hover:text-slate-900'
                }`}
                data-testid="session-type-all">
                {!sessionType && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />}
                <span className="relative z-10">All Types</span>
              </button>
              {availableSessionTypes.map((st, i) => {
                const palettes = [
                  'from-pink-400 via-fuchsia-500 to-purple-600',
                  'from-amber-400 via-orange-500 to-rose-500',
                  'from-cyan-400 via-sky-500 to-blue-600',
                  'from-emerald-400 via-teal-500 to-cyan-600',
                  'from-rose-400 via-pink-500 to-fuchsia-600',
                  'from-violet-400 via-purple-500 to-indigo-600',
                  'from-lime-400 via-green-500 to-emerald-600',
                  'from-orange-400 via-red-500 to-pink-600',
                ];
                const palette = palettes[i % palettes.length];
                const isSel = sessionType === st;
                return (
                  <button key={st} onClick={() => handleSessionTypeChange(st)}
                    className={`relative overflow-hidden px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150 ring-1 ${
                      isSel
                        ? `bg-gradient-to-br ${palette} text-white ring-white/40 shadow-[0_3px_0_-1px_rgba(0,0,0,0.18),0_6px_14px_-3px_rgba(236,72,153,0.5)] -translate-y-0.5`
                        : 'bg-white text-slate-600 ring-slate-200/70 shadow-sm hover:-translate-y-0.5 hover:shadow-[0_4px_10px_-2px_rgba(236,72,153,0.18)] hover:text-slate-900'
                    }`}
                    data-testid={`session-type-${st.replace(/\s+/g, '-')}`}>
                    {isSel && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />}
                    <span className="relative z-10">{st}</span>
                  </button>
                );
              })}
            </div>
          )}
          {category && liveClassCategories.includes(category) && (
            <div className="mt-3 inline-flex bg-white/70 backdrop-blur-md rounded-2xl p-1 ring-1 ring-slate-200/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8),0_2px_8px_-2px_rgba(0,0,0,0.08)]" data-testid="search-content-tabs">
              <button onClick={() => setViewTab('spaces')}
                className={`relative overflow-hidden px-4 py-1.5 rounded-xl text-xs font-bold transition-all duration-150 ${
                  viewTab === 'spaces'
                    ? 'bg-gradient-to-br from-indigo-400 via-violet-500 to-fuchsia-600 text-white shadow-[0_3px_0_-1px_rgba(91,33,182,0.45),0_6px_14px_-3px_rgba(168,85,247,0.55)]'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
                data-testid="content-tab-spaces">
                {viewTab === 'spaces' && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />}
                <span className="relative z-10">Spaces</span>
              </button>
              <button onClick={() => setViewTab('live')}
                className={`relative overflow-hidden px-4 py-1.5 rounded-xl text-xs font-bold transition-all duration-150 flex items-center gap-1.5 ${
                  viewTab === 'live'
                    ? 'bg-gradient-to-br from-pink-400 via-fuchsia-500 to-purple-600 text-white shadow-[0_3px_0_-1px_rgba(157,23,77,0.45),0_6px_14px_-3px_rgba(236,72,153,0.55)]'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
                data-testid="content-tab-live">
                {viewTab === 'live' && <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />}
                <VideoCamera size={14} className="relative z-10" weight={viewTab === 'live' ? 'fill' : 'regular'} />
                <span className="relative z-10">Live Sessions</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <p className="text-slate-600" data-testid="search-results-summary">
            {viewTab === 'live'
              ? (liveLoading ? 'Searching live sessiones...' : `${liveClasses.length} live session${liveClasses.length === 1 ? '' : 'es'} found`)
              : (loading ? 'Searching...' : `${total} spaces found`)
            }
            {category && ` in ${getCategoryInfo(category).name}`}
            {sessionType && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700 text-xs font-semibold">{sessionType}</span>}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="view-toggle">
              <button onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="view-list-btn">
                <ListIcon size={14} className="inline mr-1" /> List
              </button>
              <button onClick={() => setViewMode('map')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'map' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="view-map-btn">
                <MapTrifold size={14} className="inline mr-1" /> Map
              </button>
            </div>
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-48" data-testid="sort-select">
                <SortAscending size={16} className="mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recommended">Recommended</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Map View — only when on Spaces tab */}
      {viewTab === 'spaces' && viewMode === 'map' && !loading && listings.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 pb-4">
          <SearchMap
            listings={listings}
            onMarkerClick={(listing) => navigate(`/listing/${listing.listing_id}`)}
            height="450px"
          />
        </div>
      )}

      {/* Live Sessions tab */}
      {viewTab === 'live' && (
        <div className="max-w-7xl mx-auto px-4 pb-24" data-testid="live-classes-results">
          {liveLoading ? (
            <p className="text-slate-500 text-sm py-8 text-center">Loading live sessiones...</p>
          ) : liveClasses.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
              <VideoCamera size={48} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-700 mb-1">No upcoming live sessiones</h3>
              <p className="text-sm text-slate-400">Check back soon for new classes in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveClasses.map(cls => (
                <SearchLiveClassCard key={cls.class_id} cls={cls} navigate={navigate} user={user} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results Grid */}
      {viewTab === 'spaces' && (
      <div className="max-w-7xl mx-auto px-4 pb-24">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skel-card-${i}`} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-slate-200"></div>
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <SearchListingCard
                key={listing.listing_id}
                listing={listing}
                categoryInfo={getCategoryInfo(listing.category)}
                isWishlisted={wishlist.includes(listing.listing_id)}
                onWishlistToggle={toggleWishlist}
                onClick={() => navigate(`/listing/${listing.listing_id}`)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlass size={40} className="text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
            <p className="text-slate-500 mb-6">Try adjusting your search or filters</p>
            <Button onClick={() => { setQuery(''); setCategory(''); setSearchParams({}); }} variant="outline" className="rounded-full">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-96 sm:rounded-2xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-6">
              {/* Price Range */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-3 block">
                  Price Range: {getCurrencySymbol(user?.country)}{priceRange[0]} - {getCurrencySymbol(user?.country)}{priceRange[1]}
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={10000}
                  step={100}
                  className="mt-2"
                />
              </div>

              {/* Instant Booking */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Instant Booking Only</label>
                <button
                  onClick={() => setInstantBooking(!instantBooking)}
                  className={`w-12 h-6 rounded-full transition-colors ${instantBooking ? 'bg-blue-500' : 'bg-slate-200'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${instantBooking ? 'translate-x-6' : 'translate-x-1'}`}></div>
                </button>
              </div>

              <Button onClick={applyFilters} className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl h-12">
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-40">
        <div className="flex items-center justify-around h-16">
          <Link to="/buyer" className="flex flex-col items-center gap-1 text-slate-500">
            <House size={24} />
            <span className="text-xs">Home</span>
          </Link>
          <Link to="/search" className="flex flex-col items-center gap-1 text-blue-600">
            <MagnifyingGlass size={24} weight="fill" />
            <span className="text-xs font-medium">Search</span>
          </Link>
          <Link to="/bookings" className="flex flex-col items-center gap-1 text-slate-500">
            <ListIcon size={24} />
            <span className="text-xs">Bookings</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 text-slate-500">
            <House size={24} />
            <span className="text-xs">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

const SearchListingCard = ({ listing, categoryInfo, isWishlisted, onWishlistToggle, onClick }) => {
  const Icon = categoryInfo.icon;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition-shadow cursor-pointer"
      data-testid={`search-listing-${listing.listing_id}`}
    >
      <div className="relative h-48 bg-slate-100">
        {listing.images && listing.images[0] ? (
          <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={48} className="text-slate-300" weight="duotone" />
          </div>
        )}
        <button
          onClick={(e) => onWishlistToggle(listing.listing_id, e)}
          className="absolute top-3 right-3 w-8 h-8 bg-white/80 backdrop-blur rounded-full flex items-center justify-center"
          data-testid={`wishlist-${listing.listing_id}`}
        >
          <Heart size={18} weight={isWishlisted ? 'fill' : 'regular'} className={isWishlisted ? 'text-red-500' : 'text-slate-600'} />
        </button>
        {listing.instant_booking && (
          <div className="absolute bottom-3 left-3 bg-emerald-500 text-white text-xs font-medium px-2 py-1 rounded-full">
            Instant
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-blue-600 font-medium">{categoryInfo.name}</span>
        </div>
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{listing.title}</h3>
        <div className="flex items-center gap-1 text-slate-500 text-sm mb-2">
          <MapPin size={14} />
          <span className="line-clamp-1">{listing.address}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Star size={16} weight="fill" className="text-yellow-400" />
            <span className="font-medium">{listing.rating?.toFixed(1) || '0.0'}</span>
          </div>
          <div>
            <span className="font-bold text-slate-900">{getSymbol(listing.currency || listing.country)}{listing.base_price}</span>
            <span className="text-slate-500 text-sm">/{listing.pricing_model === 'hourly' ? 'hr' : 'day'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;

/* ============================
 * LIVE CLASS CARD (compact, search-results variant)
 * ============================ */
const SearchLiveClassCard = ({ cls, navigate, user }) => {
  const remaining = (cls.max_participants || 0) - (cls.booked_count || 0);
  const isFull = remaining <= 0;
  const onClick = () => {
    if (!user) {
      // Send to login with redirect back to /live-classes and auto-book this class
      navigate(`/buyer/login?redirect=${encodeURIComponent('/live-classes')}&autobook=${cls.class_id}`);
      return;
    }
    navigate(`/live-classes?autobook=${cls.class_id}`);
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`search-live-class-${cls.class_id}`}>
      <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-4 text-white">
        <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-medium">{cls.session_type}</span>
        <h3 className="font-bold text-base mt-1.5 line-clamp-1">{cls.title}</h3>
        <p className="text-xs text-white/80 mt-0.5">{cls.instructor && `by ${cls.instructor}`}</p>
      </div>
      <div className="p-4 space-y-3">
        {cls.description && <p className="text-xs text-slate-600 line-clamp-2">{cls.description}</p>}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          <span className="flex items-center gap-1"><CalendarBlank size={13} /> {cls.date}</span>
          <span className="flex items-center gap-1"><Clock size={13} /> {cls.start_time}</span>
          <span className="flex items-center gap-1"><Users size={13} /> {cls.booked_count}/{cls.max_participants}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-base font-bold text-slate-900">
            {cls.price > 0 ? `${cls.currency === 'INR' ? '₹' : '$'}${cls.price}` : 'Free'}
          </span>
          <Button size="sm" onClick={onClick} disabled={isFull}
            className={`rounded-xl text-xs h-8 ${isFull ? 'bg-slate-300' : 'bg-emerald-500 hover:bg-emerald-600'}`}
            data-testid={`search-book-class-${cls.class_id}`}>
            {isFull ? 'Full' : (user ? 'Book Spot' : 'Sign in to Book')}
          </Button>
        </div>
      </div>
    </div>
  );
};
